import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchTabsStore, normalizeSearch } from '@/store/searchTabsStore';
import { buildSearchTabTitle } from '@/utils/searchTabTitle';
import { applyMappingStateFromSearch } from '@/utils/mappingUrlState';
import { isSearchTabsEnabled } from '@/config/searchTabs';

export type NavigateSearchOptions = {
  search: string;
  title?: string;
  openInNewTab?: boolean;
  searchItems?: any[] | null;
  pathname?: string;
};

type TabActionResult = { ok: true } | { ok: false; reason: 'max_tabs' | 'not_found' };

let pendingProgrammaticSearch: string | null = null;

function beginProgrammaticNavigation(search: string) {
  pendingProgrammaticSearch = normalizeSearch(search);
  window.setTimeout(() => {
    if (pendingProgrammaticSearch === normalizeSearch(search)) {
      pendingProgrammaticSearch = null;
    }
  }, 1000);
}

export function isProgrammaticSearchNavigationPending(): boolean {
  return pendingProgrammaticSearch != null;
}

function resolveTitle(options: NavigateSearchOptions): string {
  if (options.title?.trim()) return options.title.trim();
  return buildSearchTabTitle(options.searchItems, options.search);
}

function prepareSearchDestination(search: string) {
  beginProgrammaticNavigation(search);
  applyMappingStateFromSearch(search);
}

export function useSearchTabNavigation() {
  const navigate = useNavigate();

  const openTab = useSearchTabsStore(s => s.openTab);
  const replaceActiveTab = useSearchTabsStore(s => s.replaceActiveTab);
  const activateTab = useSearchTabsStore(s => s.activateTab);
  const closeTab = useSearchTabsStore(s => s.closeTab);
  const duplicateTab = useSearchTabsStore(s => s.duplicateTab);
  const reorderTabs = useSearchTabsStore(s => s.reorderTabs);
  const syncActiveFromUrl = useSearchTabsStore(s => s.syncActiveFromUrl);
  const flushActiveSearch = useSearchTabsStore(s => s.flushActiveSearch);
  const tabs = useSearchTabsStore(s => s.tabs);
  const activeTabId = useSearchTabsStore(s => s.activeTabId);
  const maxTabs = useSearchTabsStore(s => s.maxTabs);

  const navigateSearch = useCallback(
    (options: NavigateSearchOptions): TabActionResult => {
      const pathname = options.pathname ?? '/search';
      const search = normalizeSearch(options.search);
      const title = resolveTitle(options);

      if (isSearchTabsEnabled && options.openInNewTab) {
        const result = openTab({ search, title });
        if (!result.ok) {
          return result;
        }
      } else {
        replaceActiveTab({ search, title });
      }

      prepareSearchDestination(search);
      navigate({ pathname, search });
      return { ok: true };
    },
    [navigate, openTab, replaceActiveTab],
  );

  const switchToTab = useCallback(
    (id: string) => {
      const tab = useSearchTabsStore.getState().tabs.find(t => t.id === id);
      if (!tab) return;

      flushActiveSearch(window.location.search);

      const search = normalizeSearch(tab.search);
      activateTab(id);
      prepareSearchDestination(search);
      navigate({ pathname: '/search', search });
    },
    [activateTab, flushActiveSearch, navigate],
  );

  const closeSearchTab = useCallback(
    (id: string, blankSearchFactory: () => { search: string; title: string }) => {
      flushActiveSearch(window.location.search);
      const { nextActiveId, closedActive } = closeTab(id);

      if (!closedActive) return;

      if (!nextActiveId) {
        const blank = blankSearchFactory();
        const search = normalizeSearch(blank.search);
        replaceActiveTab(blank);
        prepareSearchDestination(search);
        navigate({ pathname: '/search', search });
        return;
      }

      const next = useSearchTabsStore.getState().tabs.find(t => t.id === nextActiveId);
      if (!next) return;
      const search = normalizeSearch(next.search);
      prepareSearchDestination(search);
      navigate({ pathname: '/search', search });
    },
    [closeTab, flushActiveSearch, navigate, replaceActiveTab],
  );

  const duplicateSearchTab = useCallback(
    (id: string): TabActionResult => {
      flushActiveSearch(window.location.search);
      const result = duplicateTab(id);
      if (!result.ok) return result;

      const copy = useSearchTabsStore.getState().tabs.find(t => t.id === result.id);
      if (!copy) return { ok: false, reason: 'not_found' };

      const search = normalizeSearch(copy.search);
      prepareSearchDestination(search);
      navigate({ pathname: '/search', search });
      return { ok: true };
    },
    [duplicateTab, flushActiveSearch, navigate],
  );

  const reorderSearchTabs = useCallback(
    (fromIndex: number, toIndex: number) => reorderTabs(fromIndex, toIndex),
    [reorderTabs],
  );

  const syncFromLocation = useCallback(
    (search: string, searchItems?: any[] | null) => {
      const normalized = normalizeSearch(search);
      if (pendingProgrammaticSearch != null) {
        if (normalized === pendingProgrammaticSearch) {
          pendingProgrammaticSearch = null;
        }
        return;
      }
      const title = buildSearchTabTitle(searchItems, search);
      syncActiveFromUrl(search, title);
    },
    [syncActiveFromUrl],
  );

  const isSwitchingTab = useCallback(() => pendingProgrammaticSearch != null, []);

  return {
    navigateSearch,
    switchToTab,
    closeSearchTab,
    duplicateSearchTab,
    reorderSearchTabs,
    syncFromLocation,
    flushActiveSearch,
    isSwitchingTab,
    tabs,
    activeTabId,
    maxTabs,
    canOpenTab: tabs.length < maxTabs,
  };
}

export function isNewTabModifierClick(evt: { metaKey?: boolean; ctrlKey?: boolean }): boolean {
  return Boolean(evt.metaKey || evt.ctrlKey);
}

export function isSearchWorkspacePath(pathname: string): boolean {
  return pathname === '/search' || pathname === '/examples';
}
